import api from 'tw-oi-core/services/ContentDelivery'
import {
  saveHtmlAsset,
  cachePdfUrl,
  saveVehicleContents,
  saveDownloadMeta,
} from './offlineStorage'
import { downloadVehicleHeroShot } from './offlineHeroShot'
import {
  getPublicationsForVehicle,
  isPdf,
  getArticlesWithTags,
  containsDita,
  containsArticles,
  containsPdfs,
} from '../extras/utils'

/**
 * Builds a serializable content snapshot for offline storage
 *
 * @param {object} contents Redux contents slice
 * @param {object} ownersManualPdfs Immutable list of owner's manual PDFs
 * @returns {object} snapshot object
 */
function buildContentsSnapshot(contents, ownersManualPdfs) {
  const { topics, publications, folders, heroShots } = contents

  return {
    topics: topics ? topics.toJS() : [],
    publications: publications ? publications.toJS() : [],
    folders: folders ? folders.toJS() : [],
    heroShots: heroShots ? (heroShots.toJS ? heroShots.toJS() : heroShots) : [],
    ownersManualPdfs: ownersManualPdfs && ownersManualPdfs.size ? ownersManualPdfs.toJS() : [],
  }
}

/**
 * Collects all DITA topic objectKeys that need HTML downloads
 *
 * @param {object} topics Immutable list of topics
 * @param {object} foldersIndex Immutable map of folder index
 * @returns {Array} array of objectKey strings
 */
export function collectDitaHtmlKeys(topics, foldersIndex) {
  const keys = new Set()

  if (topics) {
    topics.forEach(topic => {
      const objectKey = topic.get ? topic.get('bodyHtml')?.get('objectKey') : topic.bodyHtml?.objectKey
      if (objectKey) keys.add(objectKey)
    })
  }

  if (foldersIndex) {
    foldersIndex.forEach(folder => {
      const bodyHtml = folder.get('bodyHtml')
      if (bodyHtml) {
        const objectKey = bodyHtml.get('objectKey')
        if (objectKey) keys.add(objectKey)
      }
    })
  }

  return [...keys]
}

/**
 * Collects HTML objectKeys for article publications
 *
 * @param {object} publications Immutable list of publications
 * @param {object} topics Immutable list of topics
 * @returns {Array} array of objectKey strings
 */
export function collectArticleHtmlKeys(publications, topics) {
  const keys = new Set()
  const pubs = getPublicationsForVehicle(publications.toJS())
  const articles = getArticlesWithTags(pubs)

  articles.forEach(article => {
    if (article.contents && article.contents.length > 0) {
      const topicId = article.contents[0].id
      const topic = topics.find(t => t.get('id') === topicId)
      if (topic) {
        const objectKey = topic.getIn(['bodyHtml', 'objectKey'])
        if (objectKey) keys.add(objectKey)
      }
    }
  })

  return [...keys]
}

/**
 * Collects all PDF urls from publications
 *
 * @param {object} publications Immutable list of publications
 * @returns {Array} array of PDF URL strings
 */
export function collectPdfUrls(publications) {
  const urls = []
  const pubs = getPublicationsForVehicle(publications.toJS())
  pubs.forEach(pub => {
    if (isPdf(pub) && pub.bodyPdf && pub.bodyPdf.url) {
      urls.push(pub.bodyPdf.url)
    }
  })
  return urls
}

/**
 * Collects printable owner's manual PDF URLs
 *
 * @param {object} ownersManualPdfs Immutable list of owner's manual PDFs
 * @returns {Array} array of PDF URL strings
 */
export function collectOwnerManualPdfUrls(ownersManualPdfs) {
  const urls = []
  if (ownersManualPdfs && ownersManualPdfs.size) {
    ownersManualPdfs.forEach(item => {
      const url = item.getIn(['bodyPdf', 'url'])
      if (url) urls.push(url)
    })
  }
  return urls
}

/**
 * Downloads a single HTML asset, retrying once on failure
 *
 * @param {string} objectKey the asset object key
 * @returns {Promise<boolean>} true on success
 */
async function fetchAndSaveHtml(objectKey) {
  try {
    const res = await api.getHtmlAsset(objectKey)
    await saveHtmlAsset(objectKey, res.data)
    return true
  } catch (e) {
    try {
      const res = await api.getHtmlAsset(objectKey)
      await saveHtmlAsset(objectKey, res.data)
      return true
    } catch (retryErr) {
      console.warn('[offline] Failed to download HTML asset:', objectKey, retryErr)
      return false
    }
  }
}

/**
 * Downloads assets in batches to avoid overwhelming the browser/server
 *
 * @param {Array} items items to download
 * @param {Function} downloadFn per-item download function
 * @param {number} batchSize batch size
 * @param {Function} onProgress called with (completed, total) after each batch
 * @returns {Promise<void>}
 */
async function downloadInBatches(items, downloadFn, batchSize, onProgress) {
  let completed = 0
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize)
    await Promise.all(batch.map(item => downloadFn(item)))
    completed += batch.length
    if (onProgress) onProgress(completed, items.length)
  }
}

/**
 * Downloads the Owner's Manual content for offline use.
 * Includes: DITA topics + their HTML assets + printable PDF
 *
 * @param {string} vehicleId vehicle identifier
 * @param {object} contents Redux contents slice
 * @param {object} ownersManualPdfs Immutable list of owner's manual PDFs
 * @param {Function} onProgress progress callback
 * @param {object} [vehicleInfo] vehicle metadata for offline vehicle selector
 * @returns {Promise<object>} result with success, htmlCount, pdfCount
 */
export async function downloadOwnersManual(
  vehicleId,
  contents,
  ownersManualPdfs,
  onProgress,
  vehicleInfo = null
) {
  const { topics, foldersIndex } = contents

  const totalSteps = { current: 0, total: 0 }
  const report = (phase) => {
    if (onProgress) onProgress({ phase, ...totalSteps })
  }

  report('Saving content structure...')
  const contentsSnapshot = buildContentsSnapshot(contents, ownersManualPdfs)
  await saveVehicleContents(vehicleId, contentsSnapshot, vehicleInfo)

  const htmlKeys = collectDitaHtmlKeys(topics, foldersIndex)
  totalSteps.total = htmlKeys.length

  report('Downloading Owner\'s Manual topics...')
  await downloadInBatches(htmlKeys, fetchAndSaveHtml, 5, (done) => {
    totalSteps.current = done
    report('Downloading Owner\'s Manual topics...')
  })

  report('Caching hero image...')
  const heroShot = await downloadVehicleHeroShot(vehicleInfo, contents.heroShots)

  const downloadedItems = ["Owner's Manual"]
  await saveDownloadMeta(vehicleId, 'owners-manual', downloadedItems, {
    objectKeys: htmlKeys,
    pdfUrls: [],
  }, vehicleInfo, heroShot)

  report('Complete')
  return { success: true, htmlCount: htmlKeys.length, pdfCount: 0, downloadedItems }
}

/**
 * Downloads ALL content for offline use.
 * Includes: DITA + Articles + PDFs. Videos are excluded.
 *
 * @param {string} vehicleId vehicle identifier
 * @param {object} contents Redux contents slice
 * @param {object} ownersManualPdfs Immutable list of owner's manual PDFs
 * @param {Function} onProgress progress callback
 * @param {object} [vehicleInfo] vehicle metadata for offline vehicle selector
 * @returns {Promise<object>} result with success, htmlCount, pdfCount
 */
export async function downloadAllContent(
  vehicleId,
  contents,
  ownersManualPdfs,
  onProgress,
  vehicleInfo = null
) {
  const { topics, publications, foldersIndex } = contents

  const totalSteps = { current: 0, total: 0 }
  const report = (phase) => {
    if (onProgress) onProgress({ phase, ...totalSteps })
  }

  report('Saving content structure...')
  const contentsSnapshot = buildContentsSnapshot(contents, ownersManualPdfs)
  await saveVehicleContents(vehicleId, contentsSnapshot, vehicleInfo)

  const ditaHtmlKeys = collectDitaHtmlKeys(topics, foldersIndex)
  const articleHtmlKeys = publications ? collectArticleHtmlKeys(publications, topics) : []
  const allHtmlKeys = [...new Set([...ditaHtmlKeys, ...articleHtmlKeys])]
  const componentPdfUrls = publications ? collectPdfUrls(publications) : []
  const allPdfUrls = [...new Set(componentPdfUrls)]

  totalSteps.total = allHtmlKeys.length + allPdfUrls.length

  report('Downloading topics and articles...')
  await downloadInBatches(allHtmlKeys, fetchAndSaveHtml, 5, (done) => {
    totalSteps.current = done
    report('Downloading topics and articles...')
  })

  report('Caching PDF documents...')
  for (const url of allPdfUrls) {
    await cachePdfUrl(url)
    totalSteps.current++
    report('Caching PDF documents...')
  }

  report('Caching hero image...')
  const heroShot = await downloadVehicleHeroShot(vehicleInfo, contents.heroShots)

  const pubs = publications ? getPublicationsForVehicle(publications.toJS()) : []
  const downloadedItems = []
  if (containsDita(pubs) || containsArticles(pubs)) {
    downloadedItems.push("Owner's Manual")
  }
  if (containsPdfs(pubs)) {
    downloadedItems.push('Component Manual')
  }

  const articles = getArticlesWithTags(pubs)
  const tags = [...new Set(articles.reduce((acc, a) => acc.concat(a.tags || []), []))]
  tags.forEach(tag => {
    if (!downloadedItems.includes(tag)) {
      downloadedItems.push(tag)
    }
  })

  await saveDownloadMeta(vehicleId, 'all-content', downloadedItems, {
    objectKeys: allHtmlKeys,
    pdfUrls: allPdfUrls,
  }, vehicleInfo, heroShot)

  report('Complete')
  return { success: true, htmlCount: allHtmlKeys.length, pdfCount: allPdfUrls.length, downloadedItems }
}
